import React from 'react';
import clsx from 'clsx';

import accountSrc from '../../../assets/icons/nav/account.png';
import browseDesignsSrc from '../../../assets/icons/nav/browse-designs.png';
import contactUsSrc from '../../../assets/icons/nav/contact-us.png';
import dashboardSrc from '../../../assets/icons/nav/dashboard.png';
import designCatalogSrc from '../../../assets/icons/nav/design-catalog.png';
import designProjectsSrc from '../../../assets/icons/nav/design-projects.png';
import designsSrc from '../../../assets/icons/nav/designs.png';
import earningsSrc from '../../../assets/icons/nav/earnings.png';
import interiorStudioSrc from '../../../assets/icons/nav/interior-studio.png';
import invoicesSrc from '../../../assets/icons/nav/invoices.png';
import licensedDesignsSrc from '../../../assets/icons/nav/licensed-designs.png';
import licensingSrc from '../../../assets/icons/nav/licensing.png';
import notificationsSrc from '../../../assets/icons/nav/notifications.png';
import ordersSrc from '../../../assets/icons/nav/orders.png';
import settingsSrc from '../../../assets/icons/nav/settings.png';
import signOutSrc from '../../../assets/icons/nav/sign-out.png';
import usersSrc from '../../../assets/icons/nav/users.png';

/**
 * The Wallmates icon set, delivered as monochrome artwork whose shape lives
 * entirely in the alpha channel.
 *
 * They are rendered as CSS masks over `bg-current` rather than as <img>, which
 * is what makes them colorable: the element paints its own text color through
 * the icon's silhouette, so `text-brand dark:text-brand-dark` (or any other
 * text color, including hover states) tints them for free. An <img> would be
 * stuck at whatever color is baked into the file.
 */
export const brandIconSources = {
    account: accountSrc,
    'browse-designs': browseDesignsSrc,
    'contact-us': contactUsSrc,
    dashboard: dashboardSrc,
    'design-catalog': designCatalogSrc,
    'design-projects': designProjectsSrc,
    designs: designsSrc,
    earnings: earningsSrc,
    'interior-studio': interiorStudioSrc,
    invoices: invoicesSrc,
    'licensed-designs': licensedDesignsSrc,
    licensing: licensingSrc,
    notifications: notificationsSrc,
    orders: ordersSrc,
    settings: settingsSrc,
    'sign-out': signOutSrc,
    users: usersSrc
} as const;

export type BrandIconName = keyof typeof brandIconSources;

export type BrandIconProps = { name: BrandIconName } & React.ComponentPropsWithoutRef<'span'>;

// #region maskStyle
const maskStyle = (src: string): React.CSSProperties => ({
    maskImage: `url(${src})`,
    WebkitMaskImage: `url(${src})`,
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskPosition: 'center'
} as React.CSSProperties);

// #region BrandIcon
export const BrandIcon: React.FunctionComponent<BrandIconProps> = (props: BrandIconProps): JSX.Element => {
    const { name, className, style, ...rest } = props;

    return (
        <span
            aria-hidden="true"
            className={clsx('inline-block shrink-0 bg-current', className ?? 'h-6 w-6')}
            style={{ ...maskStyle(brandIconSources[name]), ...style }}
            {...rest}
        />
    );
};

// #region brandIcon
/**
 * Bound component for a single icon, so it can sit in the `icon` slot of a
 * navigation item exactly where a Heroicon used to — same `{ className }` call
 * signature, no changes at the render site.
 */
export const brandIcon = (name: BrandIconName): React.FunctionComponent<React.ComponentPropsWithoutRef<'span'>> => {
    const Bound = (iconProps: React.ComponentPropsWithoutRef<'span'>) => <BrandIcon name={name} {...iconProps} />;
    Bound.displayName = `BrandIcon(${name})`;
    return Bound;
};

/** Pre-bound components for every icon in the set. */
export const BrandIcons = Object.fromEntries(
    (Object.keys(brandIconSources) as BrandIconName[]).map((name: BrandIconName) => [name, brandIcon(name)])
) as Record<BrandIconName, React.FunctionComponent<React.ComponentPropsWithoutRef<'span'>>>;
